<template>
  <div class="mask" :class="{ 'mask-hidden': isSubDialogOpen }" @mousedown.self="onClose">
    <div v-show="!isSubDialogOpen" class="edit-dialog">
      <!-- ヘッダー -->
      <div class="edit-header">
        <span class="edit-header-title">{{ $t("homePage.novelList.grouping.editTitle") }}</span>
        <button class="edit-close-button" @click="onClose">
          <IconClose class="edit-close-icon" />
        </button>
      </div>

      <!-- 空状態 -->
      <template v-if="groups.length === 0">
        <div class="edit-empty">
          <p class="edit-empty-message">{{ $t("homePage.novelList.grouping.emptyMessage") }}</p>
          <button class="edit-create-button" @click="onClickCreateGroup">
            {{ $t("homePage.novelList.grouping.createNew") }}
          </button>
        </div>
        <div class="edit-actions">
          <button class="edit-cancel-button" @click="onClose">
            {{ $t("common.close") }}
          </button>
        </div>
      </template>

      <!-- グループ一覧 -->
      <template v-else>
        <!-- 検索 -->
        <div class="edit-search">
          <IconSearch class="edit-search-icon" />
          <input
            v-model="searchQuery"
            class="edit-search-input"
            type="text"
            :placeholder="$t('homePage.novelList.grouping.searchPlaceholder')"
          />
        </div>

        <div class="edit-body">
          <SortableList
            :items="filteredGroups"
            :disabled="isSaving || !!searchQuery"
            keyAttr="groupId"
            handleClassName=".drag-handle"
            :onReorder="onReorder"
          >
            <template #default="{ item, index }">
              <div
                class="edit-group-row"
                :class="{
                  'edit-group-row-alt': index % 2 === 1,
                  'edit-group-row-disabled': isSaving,
                }"
              >
                <button
                  class="edit-drag-handle drag-handle"
                  :disabled="isSaving || !!searchQuery"
                  :aria-label="$t('homePage.novelList.grouping.reorderHandleLabel')"
                >
                  <IconHandle class="edit-icon" />
                </button>
                <span class="edit-group-name">{{ item.name }}</span>
                <div class="edit-group-actions">
                  <button
                    class="edit-icon-button"
                    :disabled="isSaving"
                    @click="onClickRename(item)"
                  >
                    <IconPencil class="edit-icon" />
                  </button>
                  <button
                    class="edit-icon-button edit-icon-danger"
                    :disabled="isSaving"
                    @click="onClickDelete(item)"
                  >
                    <IconTrash class="edit-icon" />
                  </button>
                </div>
              </div>
            </template>
          </SortableList>
        </div>

        <div class="edit-actions">
          <button class="edit-cancel-button" @click="onClose">
            {{ $t("common.close") }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { NovelGroup } from "@/lib/models";
import { Dialog } from "@/lib/utils";
import SortableList from "@/components/molecules/SortableList.vue";
import NovelGroupRenameDialog from "./NovelGroupRenameDialog.vue";
import NovelGroupDeleteConfirmDialog from "./NovelGroupDeleteConfirmDialog.vue";
import NovelGroupAddDialog, { DIALOG_RESULT_PREMIUM } from "./NovelGroupAddDialog.vue";
import SimpleDialog from "./SimpleDialog.vue";
import IconClose from "~icons/figma/Close";
import IconSearch from "~icons/figma/Search";
import IconPencil from "~icons/figma/Pencil";
import IconTrash from "~icons/figma/Trash";
import IconHandle from "~icons/figma/Handle";

export default Vue.extend<Data, Methods, Computed, {}>({
  components: { SortableList, IconClose, IconSearch, IconPencil, IconTrash, IconHandle },

  data() {
    return {
      searchQuery: "",
      isSubDialogOpen: false,
      isSaving: false,
    };
  },

  computed: {
    groups() {
      return this.$store.getters["novelGroupModule/groups"] as NovelGroup[];
    },
    filteredGroups() {
      if (!this.searchQuery) return this.groups;
      // macOS 由来の NFD グループ名でも検索がヒットするよう両辺を NFC 正規化して比較する。
      const normalizedQuery = this.searchQuery.normalize("NFC");
      return this.groups.filter((g: NovelGroup) => g.name.normalize("NFC").includes(normalizedQuery));
    },
  },

  methods: {
    async onClickRename(group: NovelGroup) {
      this.isSubDialogOpen = true;
      const renameDialog = new Dialog(NovelGroupRenameDialog);
      await renameDialog.show({
        groupId: group.groupId,
        currentName: group.name,
      });
      this.isSubDialogOpen = false;
    },

    async onClickDelete(group: NovelGroup) {
      this.isSubDialogOpen = true;
      const deleteDialog = new Dialog(NovelGroupDeleteConfirmDialog);
      await deleteDialog.show({
        groupId: group.groupId,
        groupName: group.name,
      });
      this.isSubDialogOpen = false;
    },

    async onClickCreateGroup() {
      this.isSubDialogOpen = true;
      const addDialog = new Dialog(NovelGroupAddDialog);
      const result = await addDialog.show({});
      if (result === DIALOG_RESULT_PREMIUM) {
        this.$close(DIALOG_RESULT_PREMIUM);
        return;
      }
      this.isSubDialogOpen = false;
    },

    onClose() {
      if (this.isSaving) return;
      this.$close(null);
    },

    async onReorder(items: NovelGroup[]) {
      if (this.isSaving || this.searchQuery) return;
      this.isSaving = true;
      try {
        await this.$store.dispatch("novelGroupModule/updateOrder", {
          order: items.map((g) => g.groupId),
        });
      } catch (e) {
        const errorDialog = new Dialog(SimpleDialog);
        await errorDialog.show({
          title: this.$t("homePage.novelList.grouping.reorderErrorTitle"),
          content: this.$t("homePage.novelList.grouping.reorderErrorContent"),
          isError: true,
        });
      } finally {
        await this.$store.dispatch("novelGroupModule/initialize");
        this.isSaving = false;
      }
    },
  },
});

interface Data {
  searchQuery: string;
  isSubDialogOpen: boolean;
  isSaving: boolean;
}

interface Computed {
  groups: NovelGroup[];
  filteredGroups: NovelGroup[];
}

interface Methods {
  onClickRename(group: NovelGroup): Promise<void>;
  onClickDelete(group: NovelGroup): Promise<void>;
  onClickCreateGroup(): Promise<void>;
  onClose(): void;
  onReorder(items: NovelGroup[]): Promise<void>;
}
</script>

<style lang="scss" scoped>
.mask {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  z-index: 8001;
  align-items: center;
  justify-content: center;
  background-color: var(--color-overlay);

  &.mask-hidden {
    background-color: transparent;
  }
}

.edit-dialog {
  background-color: var(--color-background-normal);
  border-radius: var(--radius-md);
  width: 340px;
  max-width: 90%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.edit-header {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 20px 24px 12px;
}

.edit-header-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
}

.edit-close-button {
  position: absolute;
  right: 16px;
  top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
}

.edit-close-icon {
  font-size: var(--font-size-sm);
  color: var(--color-text-normal);
}

/* 空状態 */
.edit-empty {
  padding: 24px 0;
  text-align: center;
}

.edit-empty-message {
  font-size: var(--font-size-sm);
  color: var(--color-text-low);
  margin-bottom: 16px;
}

.edit-create-button {
  display: inline-block;
  padding: 8px 20px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
  background-color: var(--color-surface-muted);
  border: none;
  border-radius: var(--radius-rounded);
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.8;
  }
}

/* 検索 */
.edit-search {
  display: flex;
  align-items: center;
  margin: 0 24px;
  padding: 8px 12px;
  background-color: var(--color-surface-muted);
  border-radius: var(--radius-sm);
  gap: 8px;
}

.edit-search-icon {
  font-size: var(--font-size-md);
  color: var(--color-text-low);
  flex-shrink: 0;
}

.edit-search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: var(--font-size-sm);
  color: var(--color-text-normal);
  outline: none;

  &::placeholder {
    color: var(--color-inactive);
  }
}

/* グループ一覧 */
.edit-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0 0;
}

.edit-group-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 24px;

  &.edit-group-row-alt {
    background-color: var(--color-surface-muted);
  }

  &.edit-group-row-disabled {
    opacity: 0.6;
  }
}

.edit-drag-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 28px;
  border: none;
  background: transparent;
  padding: 0;
  margin-right: 8px;
  color: var(--color-text-low);
  cursor: grab;
  flex-shrink: 0;

  &:active {
    cursor: grabbing;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
}

.edit-group-name {
  font-size: var(--font-size-sm);
  color: var(--color-text-normal);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.edit-group-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  margin-left: 12px;
}

.edit-icon-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  color: var(--color-text-normal);

  &:hover {
    opacity: 0.7;
  }

  &.edit-icon-danger {
    color: var(--color-warning);
  }
}

.edit-icon {
  font-size: var(--font-size-md);
}

/* アクション */
.edit-actions {
  padding: 12px 24px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.edit-cancel-button {
  width: 100%;
  padding: 10px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
  background-color: var(--color-surface-muted);
  border: none;
  border-radius: var(--radius-rounded);
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.85;
  }
}
</style>
